import { colors as themeColors } from '../../utils/theme';
import React, { useEffect, useState, useRef } from "react";
import {
    Box, Grid, TextField, Typography, Paper, Button, MenuItem,
    IconButton, Divider, useMediaQuery, useTheme,
    Snackbar, Alert, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, TableFooter, Dialog, DialogContent
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import DropdownProductCode from "../purchase_Order/components/DropdownProductCode";
import { useParams, useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import { getCurrentFYear, getFinancialYearBounds } from '../../utils/financialYear';

const FB_BLUE = themeColors.primary.main;
const FB_BLUE_DARK = "#0f5fcf";
const INPUT_HEIGHT = 38;

const inputSx = {
    "& .MuiOutlinedInput-root": {
        height: INPUT_HEIGHT,
        fontSize: "0.875rem",
        "& input": { padding: "0 10px" },
    },
    "& .MuiSelect-select": {
        height: `${INPUT_HEIGHT}px !important`,
        display: "flex",
        alignItems: "center",
        padding: "0 10px",
        fontSize: "0.875rem",
    },
    "& .MuiInputLabel-root": {
        fontSize: "0.875rem",
    },
};

const emptyRow = () => ({
    itemId: "", itemCode: "", description: "", unit: "",
    issue: "", received: "", Pkg: "", rate: "", amount: "", remark: ""
});

const Store = ({ mode = "add", connectionString }) => {

    const { id } = useParams();
    const navigate = useNavigate();
    const { startDate: fyStartDate, endDate: fyEndDate } = getFinancialYearBounds();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const isTablet = useMediaQuery(theme.breakpoints.down("md"));
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const { user, selectedCompany } = useAuth();

    const companyId = selectedCompany?.CompId || null;
    const gCompId = selectedCompany?.GCompId || null;
    const fYear = getCurrentFYear();
    const userId = user?.UserId || null;
    const appRegID = selectedCompany?.AppRegID || null;

    const [snackbar, setSnackbar] = useState({ open: false, message: "" });
    const [unitOptions, setUnitOptions] = useState([]);
    const [errors, setErrors] = useState({});

    const getTodayDate = () => new Date().toISOString().split("T")[0];
    const API = connectionString;

    const [form, setForm] = useState({
        receiptDate: getTodayDate(),
        receiptNum: "",
        issueById: "",
        receivedById: "",
    });

    const [current, setCurrent] = useState(emptyRow());
    const [editIndex, setEditIndex] = useState(null);
    const [rows, setRows] = useState([]);
    const [usersOptions, setUsersOptions] = useState([]);
    const [allItems, setAllItems] = useState([]);

    // ── Client/Party state ────────────────────────────────────────────────────
    const partyDropdownRef = useRef(null);
    const itemCodeRef = useRef(null);
    const [companyName, setCompanyName] = useState("");
    const [selectedClientId, setSelectedClientId] = useState(null);
    const [searchResults, setSearchResults] = useState([]);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [clientError, setClientError] = useState(false);
    const [details, setDetails] = useState([]);
    // ─────────────────────────────────────────────────────────────────────────

    const requiredAsteriskStyle = {
        '& .MuiFormLabel-asterisk': { color: 'red' },
    };

    const formatQty = (val) => {
        if (val === null || val === undefined) return "";
        const num = Number(val);
        if (num === 0) return "";
        return val;
    };

    const handleChange = field => e => {
        if (field === 'UnitId') {
            const selectedUnit = unitOptions.find(u => u.UnitId === Number(e.target.value));
            setCurrent(prev => ({
                ...prev,
                UnitId: e.target.value,
                unit: selectedUnit ? selectedUnit.unit : ''
            }));
        } else {
            setCurrent(prev => ({ ...prev, [field]: e.target.value }));
        }
    };

    useEffect(() => {
        const fetchUnits = async () => {
            try {
                const res = await axios.get(`${connectionString}/api/unit/retrive`, { params: { companyId } });
                setUnitOptions(res.data || []);
            } catch (err) { console.error("Error fetching units:", err); }
        };
        if (companyId) fetchUnits();
    }, [companyId]);

    const fetchProductionNumber = async () => {
        try {
            const res = await axios.get(`${API}/api/get-production-no`, { params: { companyId } });
            setForm(prev => ({ ...prev, receiptNum: res.data.prodNumber || "" }));
        } catch (err) { console.error("Error fetching production number:", err); }
    };

    useEffect(() => {
        if (companyId && mode === "add") fetchProductionNumber();
    }, [companyId, mode]);

    useEffect(() => {
        (async () => {
            try {
                const res = await axios.get(`${API}/api/users`, { params: { companyId } });
                setUsersOptions(res.data.data || []);
            } catch (err) { console.error('Error loading users:', err); }
        })();
    }, [API, companyId]);

    // Product list for the Item Code search. Reusable so it can be refreshed when a new
    // product is created from the "Add New Product" popup (without reloading the page).
    const fetchAllItems = async () => {
        try {
            const res = await axios.get(`${connectionString}/api/productdata/retrive`, { params: { companyId } });
            setAllItems(res.data || []);
        } catch (e) { console.error('Error fetching products:', e); }
    };

    useEffect(() => {
        fetchAllItems();
    }, [companyId]);

    useEffect(() => {
        if (user && mode === "add") {
            setForm(prev => ({ ...prev, issueById: user.UserId, receivedById: user.UserId }));
        }
    }, [user, mode]);

    useEffect(() => {
        if (mode === "add" && itemCodeRef.current) {
            itemCodeRef.current.focus();
        }
    }, [mode]);

    // ── Fetch search results for party name ───────────────────────────────────
    useEffect(() => {
        if (!companyName || !dropdownOpen) {
            setSearchResults([]);
            return;
        }
        const fetchSearch = async () => {
            try {
                const res = await axios.get(`${API}/api/company-search`, {
                    params: { field: 'FirmName', search: companyName, companyId }
                });
                const data = Array.isArray(res.data?.data) ? res.data.data : res.data;
                setSearchResults(data);
            } catch (err) {
                console.error('Error fetching company search:', err.message);
                setSearchResults([]);
            }
        };
        fetchSearch();
    }, [companyName, dropdownOpen]);

    // ── onSelectResult ────────────────────────────────────────────────────────
    const onSelectResult = (r) => {
        setSelectedClientId(r.id);
        setCompanyName(r.FirmName);
        const c = {
            city: r.City || '', state: r.state || '', mobileNo: r.MobileNo || '',
            emailId: r.EmailId || '', contactPerson: r.ContactPerson || '', gstNumber: r.GSTNo || ''
        };
        setDetails([
            { label: 'City', value: c.city },
            { label: 'State', value: c.state },
            { label: 'mobileNo', value: c.mobileNo },
            { label: 'Email', value: c.emailId },
            { label: 'ContactPerson', value: c.contactPerson },
            { label: 'GST No.', value: c.gstNumber }
        ]);
        setDropdownOpen(false);
        setClientError(false);
    };
    // ─────────────────────────────────────────────────────────────────────────

    const isItemValid =
        current.itemCode &&
        current.description &&
        current.unit &&
        (
            (current.issue !== "" && current.received === "") ||
            (current.received !== "" && current.issue === "")
        );

    useEffect(() => {
        const issue = current.issue;
        const received = current.received;
        const rate = current.rate;
        const qty = issue !== "" ? Number(issue) : received !== "" ? Number(received) : 0;
        const rateNum = Number(rate) || 0;
        const amount = qty && rateNum ? (qty * rateNum).toFixed(2) : "";
        setCurrent(prev => ({ ...prev, amount }));
    }, [current.issue, current.received, current.rate]);

    const handleFormChange = (e) => {
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
        setErrors(prev => ({ ...prev, form: "" }));
    };

    const handleCurrentChange = (field, value) => {
        setCurrent(prev => {
            let updated = { ...prev, [field]: value };
            if (field === "issue" && value !== "") updated.received = "";
            if (field === "received" && value !== "") updated.issue = "";
            return updated;
        });
    };

    const handleProductSelect = (product) => {
        setCurrent(prev => ({
            ...prev,
            itemId: product.Id,
            itemCode: product.itemCode,
            description: product.description,
            unit: product.Unit,
            rate: product.saleRate,
        }));
    };

    const addOrUpdateRow = () => {
        if (!current.itemId) {
            setSnackbar({ open: true, message: "Please select a valid item." });
            return;
        }
        if (!current.itemCode || !current.description || !current.unit) {
            setSnackbar({ open: true, message: "Item Code, Description and Unit are required." });
            return;
        }
        if (
            (current.issue === "" && current.received === "") ||
            (current.issue !== "" && current.received !== "")
        ) {
            setSnackbar({ open: true, message: "Either Issue or Received must be filled (only one)." });
            return;
        }
        if (editIndex !== null) {
            setRows(prev => prev.map((r, i) => i === editIndex ? { ...current } : r));
            setEditIndex(null);
        } else {
            setRows(prev => [{ ...current }, ...prev]);  // 🔥 Add to beginning instead
        }
        setErrors(prev => ({ ...prev, rows: "" }));
        setCurrent(emptyRow());
        if (mode === "add" && itemCodeRef.current) {
            setTimeout(() => itemCodeRef.current.focus(), 0);
        }
    };

    const editRow = (index) => {
        setCurrent({ ...rows[index] });
        setEditIndex(index);
    };

    const deleteRow = (index) => {
        setRows(prev => prev.filter((_, i) => i !== index));
        if (index === editIndex) {
            setCurrent(emptyRow());
            setEditIndex(null);
        }
    };

    const handleSubmit = async () => {
        if (!form.receiptDate || !form.receiptNum || !form.issueById || !form.receivedById) {
            setErrors(prev => ({ ...prev, form: "Please fill all required fields" }));
            return;
        }
        if (rows.length === 0) {
            setErrors(prev => ({ ...prev, rows: "Add at least one product" }));
            return;
        }
        try {
            setErrors({});
            const getUserName = (id) => usersOptions.find(u => u.UserId === id)?.employeeName || "";
            if (rows.some(r => !r.itemId)) {
                setSnackbar({ open: true, message: "All items must have valid Item selected." });
                return;
            }
            const payload = {
                meta: { gCompId, compId: companyId, fYear, addUserId: userId, appRegID },
                ...form,
                issueBy: getUserName(form.issueById),
                issueById: form.issueById,
                receivedBy: getUserName(form.receivedById),
                receivedById: form.receivedById,
                transType: "MPROD",
                bookName: "PROD",
                moduleName: "",
                clientId: selectedClientId,
                companyName,
                items: rows.map(row => ({
                    itemId: row.itemId,
                    itemCode: row.itemCode,
                    description: row.description,
                    unit: row.unit,
                    issueQty: row.issue,
                    receivedQty: row.received,
                    Pkg: row.Pkg,
                    rate: row.rate,
                    amount: row.amount,
                    remark: row.remark || ""
                })),
            };
            if (mode === "add") {
                await axios.post(`${API}/api/production/insert`, payload);
                setSnackbar({ open: true, message: 'Production saved successfully', severity: 'success' });
                setTimeout(() => { navigate(-1); }, 600);
            } else {
                await axios.put(`${API}/api/production/update/${id}`, payload);
                setSnackbar({ open: true, message: 'Production updated successfully', severity: 'success' });
                setTimeout(() => { navigate(-1); }, 600);
            }
        } catch (err) {
            console.error("Save Error:", err);
            setSnackbar({ open: true, message: 'Failed to save production', severity: 'error' });
        }
    };

    useEffect(() => {
        if (mode === "edit" && id) {
            (async () => {
                try {
                    const res = await axios.get(`${API}/api/production/getDetails/${id}`);
                    const data = res.data;
                    setForm({
                        receiptDate: data.ReceiptDate?.slice(0, 10) || "",
                        receiptNum: data.ReceiptNum || "",
                        issueById: data.IssueByID || "",
                        receivedById: data.ReceivedByID || "",
                    });
                    setCompanyName(data.companyName || data.FirmName || "");
                    setSelectedClientId(data.clientId || null);
                    if (data.details) {
                        if (Array.isArray(data.details)) {
                            setDetails(data.details);
                        } else if (typeof data.details === 'object') {
                            const arr = Object.entries(data.details).map(([label, value]) => ({ label, value }));
                            setDetails(arr);
                        }
                    }
                    const mappedRows = (data.items || []).map(r => ({
                        itemId: r.ItemId,
                        itemCode: r.itemCode,
                        description: r.description,
                        unit: r.Unit,
                        issue: formatQty(r.QtyIssue),
                        received: formatQty(r.QtyRcd),
                        Pkg: r.Pkg ?? "",
                        rate: r.Rate,
                        amount: r.Amount,
                        remark: r.Remark || ""
                    }));
                    setRows(mappedRows);
                } catch (err) { console.error("Error loading production:", err); }
            })();
        }
    }, [mode, id]);

    const totalAmount = rows.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    const totalIssue = rows.reduce((sum, r) => sum + (Number(r.issue) || 0), 0);
    const totalReceived = rows.reduce((sum, r) => sum + (Number(r.received) || 0), 0);
    const formatAmt = (val) => Number(val || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    // ── DataGrid columns ──────────────────────────────────────────────────
    const columns = [
        { field: "srNo", headerName: "#", flex: 0.3, align: "center", headerAlign: "center", renderCell: (p) => p.row.index + 1 },
        {
            field: "item", headerName: "Item", flex: 2, minWidth: 120, sortable: false, filterable: false,
            renderCell: (params) => (
                <Box sx={{ py: 0.5 }}>
                    <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: FB_BLUE, cursor: "pointer" }} onClick={() => editRow(params.row.index)}>
                        {params.row.itemCode}
                    </Typography>
                    <Typography sx={{ fontSize: "0.8125rem", color: "#616161", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.4 }}>
                        {params.row.description}
                    </Typography>
                    {params.row.remark && (
                        <Typography sx={{ fontSize: "0.8125rem", color: "#757575", whiteSpace: "normal", wordBreak: "break-word", fontStyle: "italic", mt: 0.3 }}>
                            {params.row.remark}
                        </Typography>
                    )}
                </Box>
            )
        },
        {
            field: "unit",
            headerName: "Unit",
            flex: 0.5,
            align: "center",
            headerAlign: "center",
            renderCell: (p) => <Typography sx={{ fontSize: "0.875rem" }}>{p.row.unit}</Typography>
        },
        {
            field: "issue",
            headerName: "Issue",
            flex: 0.6,
            align: "center",
            headerAlign: "center",
            renderCell: (p) => <Typography sx={{ fontSize: "0.875rem" }}>{p.row.issue || "—"}</Typography>
        },
        {
            field: "received",
            headerName: "Rcvd",
            flex: 0.6,
            align: "center",
            headerAlign: "center",
            renderCell: (p) => <Typography sx={{ fontSize: "0.875rem" }}>{p.row.received || "—"}</Typography>
        },
        {
            field: "Pkg",
            headerName: "Pkg",
            flex: 0.6,
            align: "center",
            headerAlign: "center",
            renderCell: (p) => <Typography sx={{ fontSize: "0.875rem" }}>{p.row.Pkg || "—"}</Typography>
        },
        {
            field: "rate",
            headerName: "Rate",
            flex: 0.8,
            align: "right",
            headerAlign: "right",
            renderCell: (p) => <Typography sx={{ fontSize: "0.875rem" }}>{p.row.rate ? `₹${formatAmt(p.row.rate)}` : "—"}</Typography>
        },
        {
            field: "amount",
            headerName: "Amt",
            flex: 1,
            align: "right",
            headerAlign: "right",
            renderCell: (p) => <Typography sx={{ fontSize: "0.875rem" }}>{p.row.amount ? `₹${formatAmt(p.row.amount)}` : "—"}</Typography>
        },
        {
            field: "action",
            headerName: "",
            width: 60,
            renderCell: (params) => (
                <IconButton
                    size="small"
                    onClick={() => deleteRow(params.row.index)}
                    sx={{
                        color: themeColors.error.dark,
                        "&:hover": {
                            backgroundColor: "#ffebee",
                            color: themeColors.error.darker
                        }
                    }}
                >
                    <DeleteIcon sx={{ fontSize: "1.1rem" }} />
                </IconButton>
            )
        }
    ];

    // ─────────────────────────────────────────────────────────────────────
    return (
        <Box maxWidth="lg" mx="auto" px={2} py={2}>
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.6, mt: -1.5 }}>
                <IconButton onClick={() => navigate(-1)} sx={{ position: 'absolute', left: 0, display: { xs: 'flex', md: 'none' }, color: 'text.secondary', p: 0.5, }} >
                    <ArrowBackIcon fontSize="small" />
                </IconButton>

                <Typography variant="h5" sx={{ textAlign: 'center', fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 600 }}>
                    {mode === "edit" ? "Edit Production" : "Add New Production"}
                </Typography>
            </Box>
            <Paper elevation={0} sx={{ borderRadius: "10px", border: `1px solid ${themeColors.neutral.line}`, overflow: "hidden" }}>

                {/* ── Header ───────────────────────────────────────────── */}
                <Box sx={{ px: { xs: 2, md: 3 }, pt: 2.5, pb: 2, backgroundColor: themeColors.neutral.surfaceAlt, borderBottom: `1px solid ${themeColors.neutral.line}`, borderRadius: "10px 10px 0 0" }}>
                    <Grid container spacing={1.5} alignItems="flex-start">
                        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField
                                label="Date" type="date" fullWidth name="receiptDate" size="small"
                                value={form.receiptDate} onChange={handleFormChange}
                                sx={inputSx} required InputLabelProps={{ sx: requiredAsteriskStyle, shrink: true }}
                                inputProps={{ min: fyStartDate.toISOString().slice(0, 10), max: fyEndDate.toISOString().slice(0, 10) }}
                            />
                        </Grid>
                        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField
                                label="Receipt No" type="text" fullWidth name="receiptNum" size="small" disabled
                                value={form.receiptNum} onChange={handleFormChange}
                                sx={inputSx} required InputLabelProps={{ sx: requiredAsteriskStyle }}
                            />
                        </Grid>
                        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField
                                select label="Issue By" fullWidth name="issueById" size="small"
                                value={form.issueById} onChange={handleFormChange}
                                sx={inputSx} required InputLabelProps={{ sx: requiredAsteriskStyle }}
                            >
                                {usersOptions.map(u => (
                                    <MenuItem key={u.UserId} value={u.UserId} sx={{ fontSize: "0.875rem" }}>{u.employeeName}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>
                        <Grid item size={{ xs: 12, sm: 6, md: 3 }}>
                            <TextField
                                select label="Received By" fullWidth name="receivedById" size="small"
                                value={form.receivedById} onChange={handleFormChange}
                                sx={inputSx} required InputLabelProps={{ sx: requiredAsteriskStyle }}
                            >
                                {usersOptions.map(u => (
                                    <MenuItem key={u.UserId} value={u.UserId} sx={{ fontSize: "0.875rem" }}>{u.employeeName}</MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        {/* ── Party Name field ── */}
                        <Grid item size={{ xs: 12, sm: 4, md: 3 }}>
                            <Box
                                ref={partyDropdownRef}
                                tabIndex={-1}
                                sx={{ position: 'relative', height: '100%' }}
                                onBlur={e => {
                                    if (!partyDropdownRef.current.contains(e.relatedTarget)) {
                                        setDropdownOpen(false);
                                    }
                                }}
                            >
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Party Name"
                                    variant="outlined"
                                    value={companyName}
                                    error={clientError}
                                    helperText={clientError ? "Please select a client from the list" : ""}
                                    InputLabelProps={{ shrink: Boolean(companyName) }}
                                    sx={inputSx}
                                    onBlur={() => {
                                        if (companyName && !selectedClientId) setClientError(true);
                                    }}
                                    onChange={(e) => {
                                        setCompanyName(e.target.value);
                                        setSelectedClientId(null);
                                        setDropdownOpen(true);
                                        setClientError(false);
                                    }}
                                />

                                {/* Dropdown results */}
                                {dropdownOpen && companyName && (
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            mt: 1,
                                            position: 'absolute',
                                            width: { xs: '100%', lg: '800px' },
                                            zIndex: 10,
                                            border: '1px solid rgba(0,0,0,0.2)',
                                            borderRadius: 1,
                                            maxHeight: 240,
                                            overflowY: 'auto'
                                        }}
                                    >
                                        {isSmallScreen ? (
                                            <>
                                                {searchResults.length > 0 ? (
                                                    searchResults.map((r, i) => (
                                                        <Box
                                                            key={i}
                                                            sx={{ p: 1.2, borderBottom: `1px solid ${themeColors.neutral.border}`, cursor: 'pointer' }}
                                                            onMouseDown={() => {
                                                                onSelectResult(r);
                                                                setCompanyName(r.FirmName);
                                                                setClientError(false);
                                                                setDropdownOpen(false);
                                                            }}
                                                        >
                                                            <Box sx={{ fontWeight: 600, fontSize: '0.875rem' }}>{r.FirmName}</Box>
                                                            <Box sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>GST: {r.GSTNo || '-'}</Box>
                                                            <Box sx={{ fontSize: '0.8125rem' }}>{r.ContactPerson || '-'} • {r.MobileNo || '-'}</Box>
                                                            <Box sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>{r.City || '-'}</Box>
                                                        </Box>
                                                    ))
                                                ) : (
                                                    <Box sx={{ p: 2, textAlign: 'center', color: 'error.main', fontSize: '0.875rem' }}>Client not found</Box>
                                                )}
                                            </>
                                        ) : (
                                            <Table size="small" stickyHeader>
                                                <TableHead>
                                                    <TableRow>
                                                        {['Company', 'GST No', 'Contact', 'Mobile', 'City'].map(h => (
                                                            <TableCell
                                                                key={h}
                                                                sx={{ backgroundColor: themeColors.neutral.line, fontWeight: 600, fontSize: '0.8125rem', position: 'sticky', top: 0, zIndex: 1 }}
                                                            >
                                                                {h}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {searchResults.length > 0 ? (
                                                        searchResults.map((r, i) => (
                                                            <TableRow
                                                                key={i}
                                                                hover
                                                                sx={{ cursor: 'pointer' }}
                                                                onMouseDown={() => {
                                                                    onSelectResult(r);
                                                                    setCompanyName(r.FirmName);
                                                                    setClientError(false);
                                                                    setDropdownOpen(false);
                                                                }}
                                                            >
                                                                <TableCell sx={{ fontSize: '0.8125rem' }}>{r.FirmName}</TableCell>
                                                                <TableCell sx={{ fontSize: '0.8125rem' }}>{r.GSTNo}</TableCell>
                                                                <TableCell sx={{ fontSize: '0.8125rem' }}>{r.ContactPerson}</TableCell>
                                                                <TableCell sx={{ fontSize: '0.8125rem' }}>{r.MobileNo}</TableCell>
                                                                <TableCell sx={{ fontSize: '0.8125rem' }}>{r.City}</TableCell>
                                                            </TableRow>
                                                        ))
                                                    ) : (
                                                        <TableRow>
                                                            <TableCell colSpan={5} align="center" sx={{ color: 'error.main', fontSize: '0.875rem' }}>Client not found</TableCell>
                                                        </TableRow>
                                                    )}
                                                </TableBody>
                                                <TableFooter sx={{ position: 'sticky', bottom: 0, backgroundColor: themeColors.neutral.surface }}>
                                                    <TableRow>
                                                        <TableCell colSpan={5} align="center" sx={{ color: 'primary.main', fontWeight: 600, fontSize: '0.875rem' }}>
                                                            ➕ Add New Client
                                                        </TableCell>
                                                    </TableRow>
                                                </TableFooter>
                                            </Table>
                                        )}
                                    </Paper>
                                )}
                            </Box>
                        </Grid>

                        {/* ── Address field ── */}
                        <Grid item size={{ xs: 12, sm: 8, md: 9 }}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Address"
                                variant="outlined"
                                disabled
                                value={
                                    Array.isArray(details)
                                        ? details
                                            .filter(d => d.value && d.value !== 'undefined' && d.value !== undefined)
                                            .map(d => `${d.label}: ${d.value}`)
                                            .join(', ')
                                        : ''
                                }
                                multiline
                                rows={1}
                                InputLabelProps={{ shrink: Array.isArray(details) && details.length > 0 }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        height: "auto",
                                        display: 'flex',
                                        alignItems: 'flex-start',
                                        paddingTop: '8px',
                                    },
                                    '& .MuiOutlinedInput-input': {
                                        fontSize: '0.875rem',
                                        padding: '0 10px',
                                    },
                                    '& .MuiInputLabel-root': {
                                        fontSize: '0.875rem',
                                    }
                                }}
                            />
                        </Grid>

                    </Grid>
                </Box>

                {/* ── Two-panel body ───────────────────────────────────── */}
                <Box
                    display="flex"
                    flexDirection={isTablet ? "column" : "row"}
                    sx={{
                        width: "100%",
                        boxSizing: "border-box",
                        flexWrap: isTablet ? "wrap" : "nowrap",
                        overflow: "hidden",
                        minHeight: 480,
                    }}
                >
                    {/* ── LEFT: item input form ─────────────────────────── */}
                    <Box
                        display="flex"
                        flexDirection="column"
                        gap={1.5}
                        sx={{
                            flex: "2 1 0%",
                            minWidth: 250,
                            boxSizing: "border-box",
                            borderRight: isTablet ? "none" : `1px solid ${themeColors.neutral.line}`,
                            borderBottom: isTablet ? `1px solid ${themeColors.neutral.line}` : "none",
                            px: { xs: 2, md: 3 },
                            py: 2.5,
                        }}
                    >
                        <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#757575", letterSpacing: "0.09em", textTransform: "uppercase", mb: 0.5 }}>
                            Item Details
                        </Typography>

                        <DropdownProductCode
                            ref={itemCodeRef}
                            id="itemCode-left" searchBy="code" label="Item Code"
                            options={allItems} value={current.itemCode}
                            connectionString={connectionString} onSelect={handleProductSelect}
                            onNewProductAdded={fetchAllItems}
                        />

                        <TextField label="Description" required fullWidth multiline minRows={1} size="small" placeholder="Enter product description" value={current.description} onChange={handleChange('description')} InputLabelProps={{ sx: requiredAsteriskStyle }} sx={{ '& .MuiInputBase-root:not(.MuiInputBase-multiline)': { height: 30 }, '& .MuiOutlinedInput-inputMultiline': { padding: '8px 12px', minHeight: '80px' } }} />

                        <TextField
                            label="Unit" select fullWidth size="small"
                            value={current.unit || ""} required
                            onChange={(e) => handleCurrentChange("unit", e.target.value)}
                            sx={inputSx} InputLabelProps={{ sx: requiredAsteriskStyle }}
                        >
                            <MenuItem value="" sx={{ fontSize: "0.875rem" }}>Select Unit</MenuItem>
                            {unitOptions.map((u) => (
                                <MenuItem key={u.UnitId} value={u.unit} sx={{ fontSize: "0.875rem" }}>{u.unit}</MenuItem>
                            ))}
                        </TextField>

                        <Typography sx={{ fontSize: "0.875rem", mt: -0.5, mb: -0.8 }}>
                            Issue / Received <span style={{ color: "red" }}>*</span>
                        </Typography>

                        <Box display="flex" gap={1.5}>
                            <TextField
                                label="Issue" fullWidth size="small" type="number"
                                value={current.issue}
                                inputProps={{ inputMode: "decimal", step: "any", min: 0 }}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === "" || /^\d*\.?\d*$/.test(value)) handleCurrentChange("issue", value);
                                }}
                                sx={inputSx}
                            />
                            <TextField
                                label="Received" fullWidth size="small" type="number"
                                value={current.received}
                                inputProps={{ inputMode: "decimal", step: "any", min: 0 }}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value === "" || /^\d*\.?\d*$/.test(value)) handleCurrentChange("received", value);
                                }}
                                sx={inputSx}
                            />
                        </Box>

                        <Box display="flex" gap={1.5}>
                            <TextField
                                label="Pkg / Bag" fullWidth size="small"
                                value={current.Pkg}
                                onChange={e => handleCurrentChange("Pkg", e.target.value)}
                                sx={inputSx}
                            />
                            <TextField
                                label="Rate" fullWidth size="small" type="number"
                                value={current.rate}
                                onChange={e => handleCurrentChange("rate", e.target.value === "" ? "" : Number(e.target.value))}
                                inputProps={{ inputMode: "decimal" }}
                                sx={inputSx}
                            />
                        </Box>

                        <TextField label="Amount" fullWidth size="small" disabled value={current.amount} sx={inputSx} />

                        <TextField
                            label="Remark"
                            fullWidth
                            size="small"
                            value={current.remark || ""}
                            onChange={e => handleCurrentChange("remark", e.target.value)}
                            sx={{
                                ...inputSx,
                                '& .MuiOutlinedInput-root': {
                                    height: 'auto',
                                    fontSize: '0.875rem',
                                },
                            }}
                            multiline
                            rows={2}
                        />

                        <Box display="flex" justifyContent="center" mt={0.5}>
                            <Button
                                variant="outlined"
                                startIcon={editIndex === null ? <AddIcon sx={{ fontSize: "1rem !important" }} /> : null}
                                onClick={addOrUpdateRow}
                                disabled={!isItemValid}
                                sx={{ minWidth: 180, height: INPUT_HEIGHT, fontSize: "0.875rem" }}
                            >
                                {editIndex !== null ? "Update Item" : "Add Item"}
                            </Button>
                        </Box>
                    </Box>

                    {/* ── RIGHT: DataGrid + totals ──────────────────────── */}
                    <Box
                        sx={{
                            flex: "2 1 500px",
                            minWidth: 0,
                            overflow: "hidden",
                            px: { xs: 2, md: 3 },
                            py: 2.5,
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <Typography sx={{ fontSize: "0.75rem", fontWeight: 700, color: "#757575", letterSpacing: "0.09em", textTransform: "uppercase", mb: 1.5 }}>
                            Added Items
                        </Typography>

                        {isMobile ? (
                            /* Mobile cards */
                            <Box>
                                {rows.length === 0 ? (
                                    <Typography sx={{ fontSize: "0.875rem", color: themeColors.neutral.lineStrong, textAlign: "center", py: 4 }}>
                                        No items added yet
                                    </Typography>
                                ) : rows.map((row, i) => (
                                    <Paper key={i} elevation={0} sx={{ border: `1px solid ${themeColors.neutral.line}`, borderRadius: "8px", p: 1.5, mb: 1.5 }}>
                                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                            <Typography sx={{ fontSize: "0.875rem", fontWeight: 700, color: FB_BLUE }}>{row.itemCode}</Typography>
                                            <Box>
                                                <IconButton size="small" onClick={() => editRow(i)} sx={{ color: FB_BLUE }}>
                                                    <EditIcon sx={{ fontSize: "1rem" }} />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => deleteRow(i)} sx={{ color: "#ef9a9a", "&:hover": { color: "#e53935" } }}>
                                                    <DeleteOutlineIcon sx={{ fontSize: "1rem" }} />
                                                </IconButton>
                                            </Box>
                                        </Box>
                                        <Typography sx={{ fontSize: "0.8125rem", color: "#616161", mb: 0.5 }}>{row.description}</Typography>
                                        <Grid container spacing={1}>
                                            {[["Unit", row.unit], ["Issue", row.issue], ["Received", row.received], ["Rate", row.rate], ["Amount", row.amount], ["Remark", row.remark]].map(([label, val]) => (
                                                <Grid item xs={6} key={label}>
                                                    <Typography sx={{ fontSize: "0.75rem", color: "#9e9e9e" }}>{label}</Typography>
                                                    <Typography sx={{ fontSize: "0.8125rem" }}>{val || "—"}</Typography>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Paper>
                                ))}
                            </Box>
                        ) : (
                            /* Desktop DataGrid */
                            <Box sx={{ height: 300, width: "100%" }}>
                                <DataGrid
                                    rows={rows.map((r, i) => ({ ...r, id: i, index: i }))}
                                    columns={columns}
                                    columnBuffer={2}
                                    disableColumnResize
                                    getRowHeight={() => "auto"}
                                    disableRowSelectionOnClick
                                    hideFooter
                                    sx={{
                                        border: `1px solid ${themeColors.neutral.lineStrong}`,
                                        borderRadius: 1,
                                        '& .MuiDataGrid-columnHeaders': { minHeight: 55, maxHeight: 55 },
                                        '& .MuiDataGrid-columnHeader': { backgroundColor: themeColors.neutral.gridHeader, color: themeColors.neutral.ink, fontWeight: 700, fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.06em' },
                                        '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: themeColors.neutral.surfaceAlt },
                                        '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: themeColors.neutral.surface }
                                    }}
                                />
                            </Box>
                        )}

                        {/* ── Totals summary ──────────────────────────── */}
                        <Box mt={3} sx={{ maxWidth: 400, ml: "auto" }}>
                            <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography sx={{ fontSize: "0.9375rem" }}>Total Items</Typography>
                                <Typography sx={{ fontSize: "0.9375rem" }}>{rows.length}</Typography>
                            </Grid>
                            <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography sx={{ fontSize: "0.9375rem" }}>Total Issue Qty</Typography>
                                <Typography sx={{ fontSize: "0.9375rem" }}>
                                    {totalIssue.toLocaleString("en-IN")}
                                </Typography>
                            </Grid>

                            <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                                <Typography sx={{ fontSize: "0.9375rem" }}>Total Received Qty</Typography>
                                <Typography sx={{ fontSize: "0.9375rem" }}>
                                    {totalReceived.toLocaleString("en-IN")}
                                </Typography>
                            </Grid>
                            <Divider sx={{ my: 1 }} />
                            <Grid container justifyContent="space-between" alignItems="center">
                                <Typography sx={{ fontWeight: 700, fontSize: "1rem", marginRight: "12px" }}>Total Amount</Typography>
                                <Typography sx={{ fontWeight: 700, fontSize: "1rem" }}>₹{formatAmt(totalAmount)}</Typography>
                            </Grid>
                        </Box>

                        <Box mt={2}>
                            {(errors.form || errors.rows) && (
                                <Typography color="error" sx={{ fontSize: "0.875rem" }}>{errors.form || errors.rows}</Typography>
                            )}
                        </Box>

                        <Box display="flex" justifyContent="flex-end" mt={3}>
                            <Button
                                variant="contained"
                                onClick={handleSubmit}
                                disableElevation
                                sx={{
                                    height: INPUT_HEIGHT,
                                    fontSize: "0.875rem",
                                    fontWeight: 600,
                                    borderRadius: "7px",
                                    backgroundColor: FB_BLUE,
                                    color: themeColors.neutral.surface,
                                    px: 4,
                                    "&:hover": { backgroundColor: FB_BLUE_DARK },
                                }}
                            >
                                {mode === "add" ? "Submit" : "Update"}
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Paper>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar(s => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                sx={{ whiteSpace: "pre-line" }}
            >
                <Alert severity={snackbar.severity} variant="filled" sx={{ fontSize: "0.875rem" }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Store;